import $ from 'jquery';

const CONFIG = {
    OPEN: '.js-headerNav-open',
    CLOSE: '.js-headerNav-close',
    OVERLAY: '.js-headerNav-overlay',
    DRAWER: '.js-headerNav-drawer',
    POST_LINK: '.js-headerNav-postLink',
    BODY_SELECTOR: 'headerNav-isActive',
    TRANSITION_LENGTH: 250
};

/**
 * @fileOverview View handles updating the visual elements on the page
 */
export default class MenuView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        this.init();
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        this.isEnabled = false;
        this.isActive = false;

        return this.setupHandlers()
                   .createChildren()
                   .layout()
                   .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {MenuView}
     */
    setupHandlers() {
        this.handleOpenClick = this.onOpenClick.bind(this);
        this.handleCloseClick = this.onCloseClick.bind(this);
        this.handlePostLinkClick = this.onPostLinkClick.bind(this);
        this.handleKeydown = this.onKeydown.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {MenuView}
     */
    createChildren() {
        this.$open = $(CONFIG.OPEN);
        this.$close = this.$element.find(CONFIG.CLOSE);
        this.$postLink = $(CONFIG.POST_LINK);

        return this;
    }

    /**
     * Update the DOM to reflect the current state of the model
     *
     * @method layout
     * @returns {MenuView}
     */
    layout() {
        return this;
    }

    /**
     * Disable, update the DOM, and then re-enable to create handlers for new DOM
     *
     * @method render
     */
    render() {
        this.disable();
        this.layout();
        this.enable();
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {MenuView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$open.length; i++) {
            this.$open[i].addEventListener('click', this.handleOpenClick);
        }

        for (let i = 0; i < this.$close.length; i++) {
            this.$close[i].addEventListener('click', this.handleCloseClick);
        }

        for (let i = 0; i < this.$postLink.length; i++) {
            this.$postLink[i].addEventListener('click', this.handlePostLinkClick);
        }

        document.addEventListener('keydown', this.handleKeydown);

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {MenuView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$open.length; i++) {
            this.$open[i].removeEventListener('click', this.handleOpenClick);
        }

        for (let i = 0; i < this.$close.length; i++) {
            this.$close[i].removeEventListener('click', this.handleCloseClick);
        }

        for (let i = 0; i < this.$postLink.length; i++) {
            this.$postLink[i].removeEventListener('click', this.handlePostLinkClick);
        }

        document.removeEventListener('keydown', this.handleKeydown);

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {MenuView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * Handles open click event
     *
     * @param event
     * @returns {MenuView}
     */
    onOpenClick(event) {
        if (this.isActive === false) {
            this.openMenu(this.$element);
        }

        return this;
    }

    /**
     * Handles close click event
     *
     * @param event
     * @returns {MenuView}
     */
    onCloseClick(event) {
        if (this.isActive === true) {
            this.closeMenu(this.$element);
        }

        return this;
    }

    /**
     * Handles post link click event
     *
     * @param event
     * @returns {MenuView}
     */
    onPostLinkClick(event) {
        const $target = $(event.target).closest(CONFIG.POST_LINK);

        event.preventDefault();
        if ($target.attr('href')) {
            $.post($target.attr('href'));
        }

        return this;
    }

    /**
     * Handles keydown event
     * Close modal if user hits ESC
     *
     * @param event
     * @returns {MenuView}
     */
    onKeydown(event) {
        if ((this.isActive == true) && ((!event.keyCode) || (event.keyCode === 27))) {
            this.closeMenu(this.$element);
        }

        return this;
    }

    /**
     * Opens modal
     * Configure <body> so it will not scroll behind modal
     * Fade in modal and make sure it has focus/tabindex for screen readers
     *
     * @param {jQuery|HTMLElement} $menu
     * @returns {MenuView}
     */
    openMenu($menu) {
        $menu.removeClass('u-isHiddenTablet');
        $(CONFIG.OVERLAY).animate({opacity: 1}, CONFIG.TRANSITION_LENGTH);
        $menu.css({marginRight: '-400px'}).animate({marginRight: 0}, CONFIG.TRANSITION_LENGTH);
        $('body').addClass(CONFIG.BODY_SELECTOR);

        this.isActive = true;

        return this;
    }

    /**
     * Closes menu
     *
     * @param {jQuery|HTMLElement} $menu
     * @returns {MenuView}
     */
    closeMenu($menu) {
        $menu.animate({marginRight: '-400px'}, CONFIG.TRANSITION_LENGTH, () => {
            $menu.addClass('u-isHiddenTablet');
            $menu.css({marginRight: '0'});
        });
        $(CONFIG.OVERLAY).animate({opacity: 0}, CONFIG.TRANSITION_LENGTH);
        $('body').removeClass(CONFIG.BODY_SELECTOR);

        this.isActive = false;

        return this;
    }
}
